import { reflect } from '@effector/reflect';
import { Box, BoxProps } from '@mantine/core';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { accountTreeModel } from 'entities/account';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES } from 'shared/api';
import { translated } from 'shared/lib';
import {
	accountsRoute,
	cashdesksRoute,
	accountRoute,
	gameSettingsRoute,
	usersRoute,
	gamesRoute,
	accountsReportRoute,
	shiftsReportRoute,
	jackpotsRoute,
	bonusesRoute,
	vouchersRoute,
	devicesRoute,
	playersRoute,
	playersTransactionRoute,
	accountsTransactionRoute,
} from 'shared/routing';
import { NavList, NavMenu } from 'shared/ui';
import cls from './admin-layout-nav-menu.module.scss';

const AccountancyNavListItem = reflect({
	view: NavList.Item,
	bind: {
		'data-test-id': 'admin-layout-accountancy',
		to: accountRoute,
		query: accountTreeModel.$singleSelectedNode.map((id) => (id ? { id } : null)),
		children: translated('admin-layout.accountancy'),
	},
});

export const AdminLayoutNavMenu = ({ ...props }: BoxProps) => {
	const { t } = useTranslation();
	const role = useUnit(viewerModel.$role);

	return (
		<Box
			component='nav'
			className={cls.adminLayoutNavMenu}
			{...props}
		>
			<AccountancyNavListItem />
			<NavList.Item
				to={accountsRoute}
				data-test-id='admin-layout-accounts'
			>
				{t('admin-layout.accounts')}
			</NavList.Item>
			<NavList.Item
				to={cashdesksRoute}
				data-test-id='admin-layout-cashdesks'
			>
				{t('admin-layout.cashdesks')}
			</NavList.Item>
			<NavList.Item
				to={usersRoute}
				data-test-id='admin-layout-users'
			>
				{t('admin-layout.users')}
			</NavList.Item>
			<NavList.Item
				to={devicesRoute}
				data-test-id='admin-layout-devices'
			>
				{t('admin-layout.devices')}
			</NavList.Item>
			<NavList.Item
				to={playersRoute}
				data-test-id='admin-layout-players'
			>
				{t('admin-layout.players')}
			</NavList.Item>
			<NavList.Item
				to={gamesRoute}
				data-test-id='admin-layout-games'
			>
				{t('admin-layout.games')}
			</NavList.Item>
			<NavMenu label={t('admin-layout.settings')}>
				<NavMenu.Item
					to={gameSettingsRoute}
					data-test-id='admin-layout-game-settings'
				>
					{t('admin-layout.game-settings')}
				</NavMenu.Item>
				<NavMenu.Item
					to={jackpotsRoute}
					data-test-id='admin-layout-jackpots'
				>
					{t('admin-layout.jackpots')}
				</NavMenu.Item>
				<NavMenu.Item
					to={bonusesRoute}
					data-test-id='admin-layout-bonuses'
				>
					{t('admin-layout.bonuses')}
				</NavMenu.Item>
				{role === USER_ROLES.god && (
					<NavMenu.Item
						to={vouchersRoute}
						data-test-id='admin-layout-vouchers'
					>
						{t('admin-layout.vouchers')}
					</NavMenu.Item>
				)}
			</NavMenu>
			<NavMenu label={t('admin-layout.transactions')}>
				<NavMenu.Item
					to={accountsTransactionRoute}
					data-test-id='admin-layout-accounts-transaction'
				>
					{t('admin-layout.accounts-transaction')}
				</NavMenu.Item>
				<NavMenu.Item
					to={playersTransactionRoute}
					data-test-id='admin-layout-players-transaction'
				>
					{t('admin-layout.players-transaction')}
				</NavMenu.Item>
			</NavMenu>
			<NavMenu label={t('admin-layout.reports')}>
				<NavMenu.Item
					to={accountsReportRoute}
					data-test-id='admin-layout-accounts-report'
				>
					{t('admin-layout.accounts-report')}
				</NavMenu.Item>
				<NavMenu.Item
					to={shiftsReportRoute}
					data-test-id='admin-layout-shifts-report-link'
				>
					{t('admin-layout.shifts-report')}
				</NavMenu.Item>
			</NavMenu>
		</Box>
	);
};
